import { useMemo } from 'react';
import { CheckboxGroup } from '@ds';
import { InfoBanner } from '@ui';
import { CaseCardType } from '@lp/features/CasesBoard/components/CaseCard/constants';
import { BaseCaseCard } from '@lp/features/CasesBoard/components/CaseCard/components/Cards';
import { useCandidatesList } from '@lp/features/MultiSuggest/hooks';
import { SelectCasesType } from '@lp/features/MultiSuggest/types';
import { InvalidateTargetCasesType } from '@lp/features/MultiSuggest/utils';
import { Case } from '@/api/apis/case';
import { CaseListWrapper } from './CaseListWrapper';
import { ListEmptyState } from '../ListEmptyState';

const MAX_TARGET_CASES_COUNT = 3;

type TargetCasesListProps = {
  cases: Case[];
  initialCaseId: string;
  selectedCases: Case[];
  invalidCases: InvalidateTargetCasesType[];
  onCaseSelect: (caseItem: Case) => void;
};

export const TargetCasesList = ({
  cases,
  initialCaseId,
  invalidCases,
  selectedCases,
  onCaseSelect,
}: TargetCasesListProps) => {
  const { filteredList, activeIds, searchText, onSearch } = useCandidatesList(cases, initialCaseId);

  const selectedCaseIds = useMemo(() => selectedCases.map((item) => item.caseId), [selectedCases]);

  const checkDisabledState = (caseId: string) => {
    return (
      !activeIds.has(caseId) ||
      (!selectedCases.some((c) => c.caseId === caseId) && selectedCases.length === MAX_TARGET_CASES_COUNT)
    );
  };

  const invalidCasesUnosIds = useMemo(() => {
    return invalidCases.map((c) => c.unosId).join(', ');
  }, [invalidCases]);

  return (
    <CaseListWrapper
      modalContentType={SelectCasesType.Target}
      searchText={searchText}
      isVisible
      casesCount={cases.length}
      onSearchChange={onSearch}
    >
      {!!invalidCases.length && (
        <InfoBanner key="invalid-target-cases" iconType="warning">
          {`Some selected cases ${invalidCasesUnosIds} no longer meet the target case criteria anymore. Please select another target cases or proceed with current selection.`}
        </InfoBanner>
      )}
      {filteredList.length ? (
        <CheckboxGroup as="div" value={selectedCaseIds} orientation="horizontal" className="flex gap-4 flex-wrap">
          {filteredList.map((c) => (
            <BaseCaseCard
              key={c.caseId}
              cardType={CaseCardType.Candidate}
              caseData={c}
              selectType="multiple"
              isSelected={selectedCaseIds.includes(c.caseId)}
              isDisabled={checkDisabledState(c.caseId)}
              onClick={() => onCaseSelect(c)}
            />
          ))}
        </CheckboxGroup>
      ) : (
        <ListEmptyState
          iconSize="lg"
          message="Looks like no cases are matching the target cases criteria at the moment. Please go back and try again later."
        />
      )}
    </CaseListWrapper>
  );
};
